// src/theme/tokens.ts — centralised design tokens

import { alpha, type Theme } from "@mui/material/styles";

export const uiLayers = { chrome: 1700, drawer: 1800, modal: 1810, snackbar: 1820, tooltip: 1900 } as const;

// ── Palette ──────────────────────────────────────────────────────────

export const plasmaPalette = {
  lilac: "#D8B6FF",
  violet: "#B87DFF",
  cyan: "#53CCFF",
  mint: "#56E6B2",
  ink: "#101722",
  deep: "#131D2A",
  surface: "#192434",
  violetSurface: "#2F1F44",
} as const;

export const semanticPalette = {
  dark: {
    info: "#53CCFF",
    success: "#56E6B2",
    warning: "#D8AE68",
    error: "#EF9AA2",
  },
  light: {
    info: "#217EA6",
    success: "#1F8066",
    warning: "#9B6A21",
    error: "#A74755",
  },
} as const;

export const palette = {
  dark: {
    base: plasmaPalette.ink,
    baseLighter: plasmaPalette.deep,
    surface: "#172130",
    surfaceElevated: plasmaPalette.surface,
    surfaceRaised: "#1E2A3B",
    surfaceTint: "#25324A",
    border: "rgba(216,182,255,0.095)",
    borderHover: "rgba(216,182,255,0.16)",
  },
  accent: {
    violet: plasmaPalette.violet,
    violetSoft: plasmaPalette.lilac,
    graphite: "#677187",
    silver: "#D7DEE8",
    cyan: plasmaPalette.cyan,
    blue: "#91A5D8",
    mint: plasmaPalette.mint,
    amber: semanticPalette.dark.warning,
    red: semanticPalette.dark.error,
  },
  // Light mode
  light: {
    background: "#FBF9FF",
    paper: "#FFFFFF",
    surface: "#F5F7FC",
    border: "rgba(47,31,68,0.10)",
    borderHover: "rgba(47,31,68,0.17)",
  },
  // Shared primary
  primary: {
    main: plasmaPalette.lilac,
    dark: plasmaPalette.violet,
    light: "#F1E6FF",
    contrastText: plasmaPalette.ink,
  },
  // Text
  text: {
    lightPrimary: "#0b1220",
    lightSecondary: "#606976",
    darkPrimary: "#F4F7FB",
    darkSecondary: "#D6DDE7",
    darkMuted: "#AAB5C3",
  },
} as const;

// ── Shape ────────────────────────────────────────────────────────────

export const shape = {
  borderRadiusSmall: 4, // cards, dialogs, paper
  borderRadiusMedium: 6, // buttons, chips, inputs
  borderRadiusLarge: 8, // special elements
} as const;

// ── Typography ───────────────────────────────────────────────────────

export const typographyTokens = {
  fontFamily: `"Onest Variable", "Onest", "WH UI", "Noto Sans", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`,
  monoFamily: `"IBM Plex Mono", "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace`,
  baseFontSize: 13.5,
  weights: {
    body: 460,
    medium: 580,
    semibold: 670,
    bold: 740,
    metric: 800,
  },
} as const;

// ── Shadows ──────────────────────────────────────────────────────────

export const lightShadows = [
  "none",
  "0 2px 6px rgba(0,0,0,.04)",
  "0 6px 14px rgba(0,0,0,.06)",
  "0 10px 22px rgba(0,0,0,.07)",
  ...Array(22).fill("0 16px 32px rgba(0,0,0,.08)"),
] as const;

export const darkShadows = [
  "none",
  "0 1px 2px rgba(0,0,0,.22), 0 0 0 1px rgba(255,255,255,.035)",
  "0 4px 14px rgba(0,0,0,.20), 0 0 0 1px rgba(255,255,255,.045)",
  "0 8px 26px rgba(0,0,0,.24), 0 0 0 1px rgba(255,255,255,.05)",
  ...Array(22).fill("0 12px 34px rgba(0,0,0,.28), 0 0 0 1px rgba(255,255,255,.05)"),
] as const;

// ── Glass helper ─────────────────────────────────────────────────────

export const glass = (bg: string, opacity: number = 0.96) => ({
  background: alpha(bg, opacity),
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  border: "1px solid",
});

// ── App chrome glass ─────────────────────────────────────────────────

export const appChrome = {
  backdropFilter: "blur(22px) saturate(1.35)",
  darkBorder: "rgba(216,182,255,0.13)",
  darkBorderHover: "rgba(216,182,255,0.20)",
  lightBorderOpacity: 0.08,
  lightBorderHoverOpacity: 0.11,
  surfaceOpacity: 0.62,
  surfaceOpacityExpanded: 0.74,
} as const;

export const appChromeBorderColor = (theme: Theme, hover = false) =>
  theme.palette.mode === "dark"
    ? hover
      ? appChrome.darkBorderHover
      : appChrome.darkBorder
    : alpha(
        theme.palette.text.primary,
        hover ? appChrome.lightBorderHoverOpacity : appChrome.lightBorderOpacity,
      );

export const appChromeSurfaceFill = (
  theme: Theme,
  opacity: number = appChrome.surfaceOpacity,
) =>
  theme.palette.mode === "dark"
    ? alpha(palette.dark.surface, opacity)
    : alpha(theme.palette.background.paper, opacity);

export const appChromeShadow = (theme: Theme) =>
  theme.palette.mode === "dark"
    ? "0 14px 38px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.045)"
    : "0 10px 28px rgba(15,23,42,0.1), inset 0 1px 0 rgba(255,255,255,0.72)";

// ── Brand plasma identity ────────────────────────────────────────────

export const brandPlasma = {
  color: plasmaPalette.lilac,
  direction: "forward",
  scale: 1,
} as const;

export const brandPlasmaChromeProps = {
  ...brandPlasma,
  speed: 0.62,
  opacity: 0.86,
} as const;

export const brandPlasmaAuthProps = {
  ...brandPlasma,
  speed: 0.62,
  opacity: 0.86,
  maxDpr: 0.85,
  targetFps: 24,
  iterations: 46,
} as const;

export const brandPlasmaCanvasFilter = "saturate(1.35) contrast(1.12)";

export const brandPlasmaAmbientBackground = (mode: "light" | "dark") =>
  mode === "dark"
    ? [
        "radial-gradient(78% 165% at 6% 0%, rgba(184,125,255,.46) 0%, rgba(184,125,255,.2) 30%, transparent 66%)",
        "radial-gradient(70% 145% at 80% 4%, rgba(83,204,255,.34) 0%, rgba(83,204,255,.15) 32%, transparent 64%)",
        "radial-gradient(88% 150% at 48% 102%, rgba(86,230,178,.24) 0%, rgba(86,230,178,.1) 38%, transparent 72%)",
        "linear-gradient(120deg, rgba(28,37,55,.22), rgba(47,31,68,.2) 48%, rgba(19,29,41,.16))",
      ].join(", ")
    : [
        "radial-gradient(78% 165% at 6% 0%, rgba(160,103,221,.36) 0%, rgba(160,103,221,.13) 32%, transparent 66%)",
        "radial-gradient(70% 145% at 80% 4%, rgba(45,150,220,.24) 0%, rgba(45,150,220,.1) 34%, transparent 64%)",
        "radial-gradient(88% 150% at 48% 102%, rgba(34,172,132,.16) 0%, rgba(34,172,132,.07) 40%, transparent 72%)",
        "linear-gradient(120deg, rgba(255,255,255,.34), rgba(238,232,248,.2) 50%, rgba(236,247,250,.16))",
      ].join(", ");

export const brandPlasmaAmbientLayerSx = {
  position: "absolute",
  inset: 0,
  zIndex: 0,
  pointerEvents: "none",
  opacity: (theme: Theme) => (theme.palette.mode === "dark" ? 0.9 : 0.58),
  background: (theme: Theme) =>
    brandPlasmaAmbientBackground(theme.palette.mode),
} as const;

export const brandPlasmaVeilLayerSx = {
  position: "absolute",
  inset: 0,
  zIndex: 0,
  pointerEvents: "none",
  background: (theme: Theme) =>
    theme.palette.mode === "dark"
      ? "linear-gradient(180deg, rgba(20,28,40,.32) 0%, rgba(20,28,40,.56) 100%)"
      : "linear-gradient(180deg, rgba(255,255,255,.46) 0%, rgba(255,255,255,.64) 100%)",
} as const;

export const brandPlasmaAuthVeilLayerSx = {
  ...brandPlasmaVeilLayerSx,
  background: (theme: Theme) =>
    theme.palette.mode === "dark"
      ? [
          "linear-gradient(180deg, rgba(20,28,40,.28) 0%, rgba(20,28,40,.48) 52%, rgba(12,18,27,.66) 100%)",
          "radial-gradient(80% 72% at 50% 18%, rgba(216,182,255,.08) 0%, transparent 66%)",
        ].join(", ")
      : [
          "linear-gradient(180deg, rgba(255,255,255,.44) 0%, rgba(255,255,255,.62) 100%)",
          "radial-gradient(80% 72% at 50% 18%, rgba(216,182,255,.1) 0%, transparent 66%)",
        ].join(", "),
} as const;

// ── Plasma surface hover ─────────────────────────────────────────────

type SurfacePlasmaHoverOptions = {
  opacity?: number;
  activeOpacity?: number;
};

export const surfacePlasmaHoverCss = (
  mode: "light" | "dark",
  options: SurfacePlasmaHoverOptions = {},
) => {
  const dark = mode === "dark";
  const idleOpacity = options.opacity ?? 0;
  const activeOpacity = options.activeOpacity ?? (dark ? 0.22 : 0.16);

  return {
    position: "relative",
    overflow: "hidden",
    isolation: "isolate",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      zIndex: 0,
      pointerEvents: "none",
      opacity: idleOpacity,
      background: brandPlasmaAmbientBackground(mode),
      backgroundSize: "118% 118%, 112% 112%, 116% 116%, 100% 100%",
      backgroundPosition: "12% 0%, 88% 8%, 48% 100%, 0% 0%",
      transition: "opacity .22s ease, background-position .7s ease",
    },
    "&:hover::after, &:focus-within::after": {
      opacity: activeOpacity,
      backgroundPosition: "24% 8%, 76% 18%, 54% 86%",
      animation: "wh-surface-plasma-drift 3.2s ease-in-out infinite alternate",
    },
    "& > *": {
      position: "relative",
      zIndex: 1,
    },
  } as const;
};

export const surfacePlasmaHoverSx = (
  theme: Theme,
  options: SurfacePlasmaHoverOptions = {},
) => surfacePlasmaHoverCss(theme.palette.mode, options);

// ── Dark-mode background gradient ────────────────────────────────────

export const darkGradientBackground = [
  "radial-gradient(115% 92% at 50% -18%, rgba(83,204,255,.055) 0%, transparent 58%)",
  "radial-gradient(92% 78% at 0% 10%, rgba(184,125,255,.052) 0%, transparent 66%)",
  "radial-gradient(86% 72% at 100% 0%, rgba(86,230,178,.034) 0%, transparent 68%)",
  "linear-gradient(180deg, #101722 0%, #0F1621 48%, #101722 100%)",
].join(", ");

// ── Scrollbar utility ────────────────────────────────────────────────

export const scrollbarSx = (mode: "light" | "dark") =>
  ({
    scrollbarWidth: "thin" as const,
    scrollbarGutter: "stable" as const,
    "&::-webkit-scrollbar": { width: 8 },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor:
        mode === "dark" ? alpha(palette.primary.main, 0.24) : "rgba(0,0,0,.22)",
      borderRadius: 8,
      border: "2px solid transparent",
      backgroundClip: "content-box",
      minHeight: 28,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor:
        mode === "dark" ? alpha(palette.primary.main, 0.36) : "rgba(0,0,0,.30)",
    },
  }) as const;
